import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { comics } from '@/mocks/comics';
import { categories } from '@/mocks/categories';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);

  if (!category) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground-900 font-heading mb-2">分类未找到</h1>
            <p className="text-foreground-500 mb-4">该分类不存在或已被移除</p>
            <Link to="/" className="text-primary-500 hover:text-primary-600 cursor-pointer">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const categoryComics = comics.filter((c) => c.categorySlug === slug);

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <div className="px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '分类' },
              { label: category.name },
            ]}
          />
        </div>

        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100" aria-label="分类导航">
          <div className="text-center mb-10">
            <span className="inline-block border border-secondary-300 text-secondary-600 text-xs font-medium px-3 py-1 rounded-md mb-3">
              漫画分类
            </span>
            <h1 className="text-2xl md:text-4xl font-bold text-foreground-900 font-heading mb-2">
              {category.name}
            </h1>
            <p className="text-foreground-500 text-sm">{category.description}</p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`group rounded-lg p-5 transition-all duration-300 cursor-pointer ${
                  cat.slug === slug
                    ? 'bg-primary-500'
                    : 'bg-background-50 hover:bg-primary-500'
                }`}
              >
                <div className={`w-10 h-10 flex items-center justify-center rounded-lg mb-3 transition-colors ${
                  cat.slug === slug ? 'bg-white/20' : 'bg-secondary-100 group-hover:bg-white/20'
                }`}>
                  <i className={`${cat.icon} text-lg transition-colors ${
                    cat.slug === slug ? 'text-white' : 'text-secondary-600 group-hover:text-white'
                  }`}></i>
                </div>
                <h3 className={`text-sm font-semibold transition-colors ${
                  cat.slug === slug ? 'text-white dark:text-foreground-950' : 'text-foreground-900 group-hover:text-white dark:group-hover:text-foreground-950'
                }`}>
                  {cat.name}
                </h3>
                <p className={`text-xs mt-1 transition-colors ${
                  cat.slug === slug ? 'text-white/70 dark:text-foreground-950/70' : 'text-foreground-500 group-hover:text-white/70 dark:group-hover:text-foreground-950/70'
                }`}>
                  {cat.count} 部
                </p>
              </Link>
            ))}
          </div>
        </section>

        <section className="py-10 md:py-14 px-4 md:px-6" aria-label={`${category.name}漫画列表`}>
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">
                全部{category.name}漫画
              </h2>
              <p className="text-foreground-500 text-sm mt-1">共 {categoryComics.length} 部作品</p>
            </div>
          </div>

          {categoryComics.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {categoryComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-foreground-500">该分类下暂无漫画，敬请期待！</p>
            </div>
          )}
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6 relative overflow-hidden" aria-label="CTA区域">
          <img
            src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20style%20illustration%20with%20various%20comic%20characters%20in%20dynamic%20poses%2C%20colorful%20and%20vibrant%2C%20anime%20inspired%20art%2C%20wide%20composition%2C%20professional%20webtoon%20quality%2C%20energetic%20atmosphere&width=1920&height=600&seq=cta-bg-category&orientation=landscape"
            className="absolute inset-0 w-full h-full object-cover object-top"
            alt=""
          />
          <div className="absolute inset-0 bg-accent-950/85"></div>
          <div className="relative z-10 text-center max-w-2xl mx-auto">
            <h2 className="text-2xl md:text-4xl font-bold text-white font-heading mb-3">
              没找到想要的？
            </h2>
            <p className="text-white/70 text-sm md:text-base mb-8">
              我们每周都在更新新的漫画作品，总有一部适合你
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-primary-500 text-white dark:text-foreground-950 text-sm font-medium px-8 py-3 rounded-md hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              返回首页探索更多
              <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
            </Link>
          </div>
        </section>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${category.name} - 韩国漫画`,
            description: `${category.description}，共${category.count}部韩国漫画作品。`,
            url: `https://www.example.com/category/${slug}`,
            inLanguage: 'zh-CN',
            isPartOf: {
              '@type': 'WebSite',
              name: '韩漫大全',
              url: 'https://www.example.com/',
            },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.example.com/' },
                { '@type': 'ListItem', position: 2, name: '分类', item: 'https://www.example.com/category/fantasy' },
                { '@type': 'ListItem', position: 3, name: category.name },
              ],
            },
          }),
        }}
      />
    </>
  );
}